import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid, Checkbox, FormControl, FormControlLabel } from '@mui/material';
import useIsMobile from '../../../../hooks/useIsMobile';
import Select from '../../../../main/apps/FunctionalApps/schedule-list-view/common/filter-form/SelectUsers';

const SelectUsers = ({ name, props, valueRef, optionsRef }) => {
    const {
        control,
        selectedValues,
        getValues,
        setValue,
        watch,
        formState: { defaultValues },
    } = useFormContext();
    const referenced = watch(valueRef?.referenceField);
    const hasUpdated = useRef(false);
    const [showAll, setShowAll] = useState(false);
    const isMobile = useIsMobile();
    const referencedOptions = selectedValues[optionsRef?.referenceField]?.[optionsRef?.referenceKey];
    const options = useMemo(() => {
        if (optionsRef == null) return;
        const optionsSet = new Set([...(getValues(name) ?? []), ...(referencedOptions ?? [])]);
        return Array.from(optionsSet);
    }, [referencedOptions, getValues(name)]);
    const hasHiddenOptions = options != null;

    useEffect(() => {
        if (valueRef == null) return;

        const referenceFieldValue = getValues(valueRef?.referenceField);
        const referenceFieldDefaultValue = defaultValues?.[valueRef?.referenceField];

        // Ignore all updates when alwaysOverwrite has been enabled and the referenceField hasn't been changed
        if (valueRef?.alwaysOverwrite && !hasUpdated.current && referenceFieldValue === referenceFieldDefaultValue) {
            return;
        }

        // Only update values if the field is not the same as the defaultValue
        const existingValue = getValues(name);
        const defaultValue = defaultValues?.[name];
        const isDefault = existingValue && JSON.stringify(existingValue) === JSON.stringify(defaultValue);

        if (referenced && (valueRef?.alwaysOverwrite || !isDefault)) {
            const reference = selectedValues?.[valueRef?.referenceField];
            const referenceValue = reference?.[valueRef?.referenceKey];
            setValue(name, referenceValue);
        }

        hasUpdated.current = true;
    }, [referenced]);

    return (
        <FormControl fullWidth style={{ margin: '8px 0' }}>
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Grid xs={hasHiddenOptions && !isMobile ? 8 : 12} sx={{ mr: 1 }}>
                            <Select
                                value={field.value ?? []}
                                key={referenced}
                                onChange={(data) => {
                                    const value = data.map((user) => user._id ?? user.id ?? user);
                                    field.onChange(value);
                                }}
                                options={options}
                                showAll={showAll}
                                {...props}
                                // wrap
                            />
                        </Grid>
                        {hasHiddenOptions && (
                            <Grid xs={4} sx={{ ml: 1 }}>
                                <FormControlLabel
                                    label="Show all?"
                                    slotProps={{ typography: { noWrap: true, variant: 'caption' } }}
                                    control={
                                        <Checkbox
                                            checked={showAll}
                                            onChange={({ target }) => setShowAll(target.checked)}
                                            size="small"
                                            // wrap
                                        />
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            />
        </FormControl>
    );
};

export default SelectUsers;
